import { APP_QUIZ_CLAIM, APP_QUIZ_DEVICE } from '@common/constants';
import {
  Quiz,
  QuizAnswers,
  QuizAttempt,
  QuizClaimStatus,
  QuizPrize,
  QuizQuestion,
  QuizStartResponse,
} from '@features/quiz/api/quizApi.type';
import { format, fromUnixTime } from 'date-fns';
import { ru } from 'date-fns/locale';

export interface QuizPendingClaim {
  token: string;
  quizId: number;
  sum: number | string | null;
  currencyId: number | null;
  at: number;
}

export type QuizOutcome =
  | { kind: 'granted' }
  | { kind: 'refused', status: QuizClaimStatus }
  | { kind: 'claim' }
  | { kind: 'guest' }
  | { kind: 'failed', right: number, total: number };

export type QuizProblem =
  | { messageId: string, text?: undefined, retry: boolean }
  | { text: string, messageId?: undefined, retry: boolean };

interface CurrencyLike {
  id: number;
  code: string;
}

const TOKEN_RE = /^[0-9a-f]{32}$/;
const REFUSALS: QuizClaimStatus[] = [
  'not_passed',
  'expired',
  'quiz_gone',
  'not_found',
  'country',
  'own_attempt',
  'already_claimed',
  'bonus_disabled',
  'no_currency_condition',
  'welcome_taken',
  'failed',
];
const RULES = [ 'QUIZ_RULE_ALL', 'QUIZ_RULE_ONCE', 'QUIZ_RULE_GUEST' ];
const RETRY_STATUSES = [ 401, 408, 429 ];

let sessionDeviceKey: string | null = null;

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    return;
  }
}

export function randomDeviceKey(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function quizDeviceKey(): string {
  const stored = readStorage(APP_QUIZ_DEVICE);
  if (stored && TOKEN_RE.test(stored)) {
    sessionDeviceKey = stored;
    return stored;
  }
  if (sessionDeviceKey === null) {
    sessionDeviceKey = randomDeviceKey();
  }
  writeStorage(APP_QUIZ_DEVICE, sessionDeviceKey);
  return sessionDeviceKey;
}

export function readPendingClaim(): QuizPendingClaim | null {
  const raw = readStorage(APP_QUIZ_CLAIM);
  if (!raw) {
    return null;
  }
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object' || typeof value.token !== 'string' || !TOKEN_RE.test(value.token)) {
      return null;
    }
    return {
      token: value.token,
      quizId: Number(value.quizId) || 0,
      sum: value.sum ?? null,
      currencyId: value.currencyId === null || value.currencyId === undefined ? null : Number(value.currencyId),
      at: Number(value.at) || 0,
    };
  } catch {
    return null;
  }
}

export function savePendingClaim(attempt: QuizAttempt, now: number): boolean {
  if (!TOKEN_RE.test(attempt.token)) {
    return false;
  }
  const claim: QuizPendingClaim = {
    token: attempt.token,
    quizId: attempt.quiz?.id ?? 0,
    sum: attempt.quiz?.prize?.sum ?? null,
    currencyId: attempt.quiz?.prize?.currencyId ?? null,
    at: now,
  };
  return writeStorage(APP_QUIZ_CLAIM, JSON.stringify(claim));
}

export function clearPendingClaim(token?: string): void {
  if (token !== undefined) {
    const current = readPendingClaim();
    if (current && current.token !== token) {
      return;
    }
  }
  removeStorage(APP_QUIZ_CLAIM);
}

export function isAttempt(response: QuizStartResponse): response is QuizAttempt {
  return response.status !== 'claimed';
}

export function parseQuizId(value: string | undefined): number | null {
  if (!value || !/^[1-9][0-9]*$/.test(value)) {
    return null;
  }
  return Number(value);
}

export function answersPayload(questions: QuizQuestion[], picked: Record<number, number>): QuizAnswers | null {
  if (questions.length === 0) {
    return null;
  }
  const answers: QuizAnswers = {};
  for (const question of questions) {
    const optionId = picked[question.id];
    if (optionId === undefined || !question.options.some((option) => option.id === optionId)) {
      return null;
    }
    answers[String(question.id)] = optionId;
  }
  return answers;
}

export function quizProgress(index: number, total: number, answered: boolean): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round(((index + (answered ? 1 : 0)) / total) * 100));
}

export function claimMessageId(status: string | null | undefined): string {
  if (status === 'granted') {
    return 'QUIZ_GRANTED';
  }
  if (status && (REFUSALS as string[]).includes(status)) {
    return `QUIZ_CLAIM_${status}`;
  }
  return 'QUIZ_ERROR';
}

export function isFinalClaimError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const status = (error as { status?: unknown }).status;
  if (status === 'VALIDATION_ERROR') {
    return true;
  }
  return typeof status === 'number' && status >= 400 && status < 500 && !RETRY_STATUSES.includes(status);
}

export function quizProblem(error: unknown): QuizProblem {
  const { status, data } = (error && typeof error === 'object' ? error : {}) as { status?: unknown, data?: unknown };
  if (status === 'VALIDATION_ERROR') {
    if (typeof data === 'string') {
      return { messageId: data === 'QUIZ_RATE' ? 'QUIZ_RATE' : 'QUIZ_UNAVAILABLE', retry: false };
    }
    const quiz = data && typeof data === 'object' ? (data as Record<string, unknown>).quiz : undefined;
    if (Array.isArray(quiz) && typeof quiz[0] === 'string' && quiz[0].trim() !== '') {
      return { text: quiz[0], retry: false };
    }
    return { messageId: 'QUIZ_UNAVAILABLE', retry: false };
  }
  if (status === 403 || status === 404) {
    return { messageId: 'QUIZ_UNAVAILABLE', retry: false };
  }
  return { messageId: 'QUIZ_ERROR', retry: true };
}

export function quizOutcome(attempt: QuizAttempt, loggedIn: boolean): QuizOutcome | null {
  if (attempt.status === 'failed') {
    return { kind: 'failed', right: attempt.rightCount ?? 0, total: attempt.total };
  }
  if (attempt.status !== 'passed') {
    return null;
  }
  if (attempt.claim) {
    if (!loggedIn) {
      return { kind: 'refused', status: 'already_claimed' };
    }
    return attempt.claim.status === 'granted' ? { kind: 'granted' } : { kind: 'refused', status: attempt.claim.status };
  }
  if (loggedIn) {
    return { kind: 'claim' };
  }
  return attempt.claimable ? { kind: 'guest' } : { kind: 'refused', status: 'already_claimed' };
}

export function formatQuizSum(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return String(value);
  }
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

export function currencyCodeFor(
  currencyId: number | null | undefined,
  userCurrency: CurrencyLike | null | undefined,
  currencies: CurrencyLike[] | null | undefined,
): string {
  if (currencyId === null || currencyId === undefined) {
    return userCurrency?.code ?? '';
  }
  if (userCurrency && userCurrency.id === currencyId) {
    return userCurrency.code;
  }
  return (currencies ?? []).find((currency) => currency.id === currencyId)?.code ?? '';
}

export function prizeLabel(prize: QuizPrize | null | undefined, code: string): string {
  if (!prize) {
    return '';
  }
  const sum = formatQuizSum(prize.sum);
  return code ? `${sum} ${code}` : sum;
}

export function isQuizClaimed(quiz: Quiz | null | undefined): boolean {
  return quiz?.claimed === true;
}

export function quizRuleIds(loggedIn: boolean): string[] {
  return loggedIn ? RULES.filter((rule) => rule !== 'QUIZ_RULE_GUEST') : RULES;
}

export function formatQuizUntil(endsAt: number, locale: string): string {
  const date = fromUnixTime(endsAt);
  return locale === 'ru' ? format(date, 'd MMMM, HH:mm', { locale: ru }) : format(date, 'dd.MM.yyyy, HH:mm');
}
